import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import logger from '../utils/logger';

interface PageContainerProps {
  children: React.ReactNode;
  pagePath: string;
  onReactivate?: () => void;
}

const PageContainer: React.FC<PageContainerProps> = ({ 
  children, 
  pagePath,
  onReactivate
}) => {
  const location = useLocation();
  const [hasBeenMounted, setHasBeenMounted] = useState(false);
  const [wasInactive, setWasInactive] = useState(false);
  const [mountTime, setMountTime] = useState<number>(Date.now());
  
  // Use refs to avoid infinite loops
  const lastAuthCheck = useRef<string>('');
  const safetyIntervalRef = useRef<NodeJS.Timeout | null>(null);
  
  // Get auth state from Redux - but don't put in useEffect deps
  const authUser = useSelector((state: any) => state?.rootReducers?.auth?.user);
  const authToken = localStorage.getItem("auth_token");
  
  // EXACT route matching
  const isActive = location.pathname === pagePath;
  
  // 🎯 MAIN EFFECT - Only depend on route changes
  useEffect(() => {
    console.group('🔄 PageContainer Route Change');
    logger.log('📍 Current location:', location.pathname);
    logger.log('📄 Page path:', pagePath);
    logger.log('✅ Is active (exact):', isActive);
    logger.log('🏗️ Has been mounted:', hasBeenMounted);
    console.groupEnd();
    
    if (isActive && !hasBeenMounted) {
      logger.log(`🎯 MOUNTING PAGE: ${pagePath} for the first time`);
      setHasBeenMounted(true);
      setMountTime(Date.now());
    }
    
    if (isActive) {
      logger.log(`👀 PAGE ACTIVATED: ${pagePath}`);
      
      if (wasInactive && onReactivate) {
        logger.log(`🔄 REACTIVATING: ${pagePath} - triggering refresh`);
        onReactivate();
        setWasInactive(false);
      }
    } else if (hasBeenMounted) {
      logger.log(`💤 PAGE HIDDEN: ${pagePath} (staying mounted)`);
      setWasInactive(true);
    }
  }, [isActive, hasBeenMounted, pagePath, wasInactive, onReactivate]); 
  // 🚨 REMOVED authUser, authToken from deps

  // 🔐 AUTH CLEANUP - Separate effect with manual checking
  useEffect(() => {
    const checkAuth = () => {
      const currentAuthState = `${!!authUser}-${!!authToken}`;
      
      // Only act if auth state actually changed
      if (currentAuthState !== lastAuthCheck.current) {
        lastAuthCheck.current = currentAuthState;
        
        const isAuthenticated = !!(authUser && authToken);
        logger.log('🔐 AUTH STATE CHANGED:', {
          isAuthenticated,
          hasReduxUser: !!authUser,
          hasToken: !!authToken,
          pagePath
        });
        
        if (!isAuthenticated && hasBeenMounted) {
          logger.log(`🔐 AUTH LOST: Cleaning up ${pagePath}`);
          setHasBeenMounted(false);
          setWasInactive(false);
          setMountTime(Date.now());
        }
      }
    };

    // Check auth on every location change (not on every render)
    checkAuth();
  }, [location.pathname]); // Only check on route changes

  // ⏰ 30-MINUTE SAFETY REMOUNT
  useEffect(() => {
    if (hasBeenMounted) {
      // Clear any existing interval
      if (safetyIntervalRef.current) {
        clearInterval(safetyIntervalRef.current);
      }
      
      safetyIntervalRef.current = setInterval(() => {
        const timeSinceMounted = (Date.now() - mountTime) / (1000 * 60);
        
        if (timeSinceMounted >= 720) { // 12 hours = 720 minutes
          logger.log(`⏰ SAFETY REMOUNT: ${pagePath} after ${(timeSinceMounted/60).toFixed(1)} hours`);
          setHasBeenMounted(false);
          if (safetyIntervalRef.current) {
            clearInterval(safetyIntervalRef.current);
            safetyIntervalRef.current = null;
          }
        }
  }, 30 * 60 * 1000); // Check every 30 minutes (more efficient for 12-hour cycles)
      
      return () => {
        if (safetyIntervalRef.current) {
          clearInterval(safetyIntervalRef.current);
          safetyIntervalRef.current = null;
        }
      };
    }
  }, [hasBeenMounted, mountTime, pagePath]);

  // Don't render until first visit
  if (!hasBeenMounted) {
    logger.log(`⏸️ NOT RENDERING: ${pagePath} (not visited yet)`);
    return null;
  }

  logger.log(`🎨 RENDERING: ${pagePath} - Display: ${isActive ? 'VISIBLE' : 'HIDDEN'}`);

  return (
    <div 
      style={{ 
        display: isActive ? 'block' : 'none',
        height: '100vh',
        width: '100%'
      }}
      data-page={pagePath}
      data-active={isActive}
    >
      {children}
    </div>
  );
};

export default PageContainer;